(() => {
  'use strict';

  const root = document.documentElement;
  const body = document.body;
  const toast = document.getElementById('VekoToast');
  let toastTimer;

  const routeWithJson = (route) => {
    if (!route) return '/cart/add.js';
    return route.endsWith('.js') ? route : `${route}.js`;
  };

  const joinRoute = (base, path = '') => `${String(base || '/').replace(/\/+$/, '')}/${String(path).replace(/^\/+/, '')}`;

  const showToast = (message, isError = false) => {
    if (!toast) return;
    const text = toast.querySelector('.toast__text');
    const icon = toast.querySelector('.toast__icon');
    if (text) text.textContent = message;
    toast.classList.toggle('toast--error', isError);
    if (icon) icon.hidden = isError;
    toast.classList.add('is-visible');
    body.classList.add('is-toast-visible');
    syncChatOffset();
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => {
      toast.classList.remove('is-visible');
      body.classList.remove('is-toast-visible');
      window.requestAnimationFrame(() => syncChatOffset());
    }, 4200);
  };

  const updateCartCount = async () => {
    try {
      const cartUrl = routeWithJson(window.vekoRoutes?.cart || '/cart');
      const response = await fetch(cartUrl, {
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) return;
      const cart = await response.json();
      document.querySelectorAll('[data-cart-count]').forEach((count) => {
        count.textContent = cart.item_count;
        count.classList.toggle('is-empty', cart.item_count === 0);
      });
    } catch (error) {
      // The add-to-cart action already succeeded; a count refresh failure is non-blocking.
    }
  };

  const setFormLoading = (form, loading) => {
    const submitButtons = Array.from(form.querySelectorAll('button[type="submit"]'));
    if (form.id) {
      document.querySelectorAll('button[type="submit"][form]').forEach((button) => {
        if (button.getAttribute('form') === form.id) submitButtons.push(button);
      });
    }
    [...new Set(submitButtons)].forEach((button) => {
      button.classList.toggle('is-loading', loading);
      button.setAttribute('aria-busy', String(loading));
      if (loading) {
        button.dataset.wasDisabled = String(button.disabled);
        button.disabled = true;
      } else {
        button.disabled = button.dataset.wasDisabled === 'true';
        delete button.dataset.wasDisabled;
      }
    });
  };

  const showProductFormSuccess = (form) => {
    if (!form.classList.contains('product-form')) return;
    const successLabel = window.vekoStrings?.added || 'Added to cart.';
    const textNode = form.querySelector('[data-add-to-cart-text]');
    const submitButton = form.querySelector('[data-add-to-cart]');
    const stickyButton = form.id
      ? document.querySelector(`[data-sticky-add][form="${CSS.escape(form.id)}"]`)
      : null;
    const targets = [submitButton, stickyButton].filter(Boolean);
    if (textNode && !textNode.dataset.defaultText) textNode.dataset.defaultText = textNode.textContent.trim();
    targets.forEach((button) => {
      if (!button.dataset.defaultText) button.dataset.defaultText = button.textContent.trim();
      button.classList.add('is-success');
    });
    if (textNode) textNode.textContent = successLabel;
    if (stickyButton) stickyButton.textContent = successLabel;
    window.setTimeout(() => {
      if (textNode?.dataset.defaultText) textNode.textContent = textNode.dataset.defaultText;
      if (stickyButton?.dataset.defaultText) stickyButton.textContent = stickyButton.dataset.defaultText;
      targets.forEach((button) => button.classList.remove('is-success'));
    }, 1800);
  };

  const addToCart = async (form) => {
    if (form.dataset.ajaxSubmitting === 'true') return;
    form.dataset.ajaxSubmitting = 'true';
    setFormLoading(form, true);
    try {
      const response = await fetch(routeWithJson(window.vekoRoutes?.cartAdd), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: new FormData(form)
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.description || window.vekoStrings?.error || 'Fehler');
      }
      await updateCartCount();
      showProductFormSuccess(form);
      showToast(window.vekoStrings?.added || 'Added to cart.');
    } catch (error) {
      showToast(error.message || window.vekoStrings?.error || 'Das hat leider nicht funktioniert.', true);
    } finally {
      setFormLoading(form, false);
      delete form.dataset.ajaxSubmitting;
    }
  };

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const isQuickAdd = form.closest('[data-quick-add]');
    const isProductForm = form.classList.contains('product-form');
    if (!isQuickAdd && !isProductForm) return;
    if (event.submitter && event.submitter.name === 'checkout') return;
    event.preventDefault();
    if (form.dataset.ajaxSubmitting === 'true') return;
    addToCart(form);
  });

  document.addEventListener('click', (event) => {
    const quantityButton = event.target.closest('[data-quantity-minus], [data-quantity-plus]');
    if (quantityButton) {
      const quantity = quantityButton.closest('.quantity');
      const input = quantity?.querySelector('[data-quantity-input]');
      if (!input) return;
      const current = Number.parseInt(input.value, 10) || 0;
      const min = Number.parseInt(input.min, 10);
      const minimum = Number.isNaN(min) ? 0 : min;
      const max = Number.parseInt(input.max, 10);
      const maximum = Number.isNaN(max) ? Number.POSITIVE_INFINITY : max;
      const requested = quantityButton.hasAttribute('data-quantity-plus') ? current + 1 : Math.max(minimum, current - 1);
      input.value = String(Math.min(maximum, requested));
      const status = quantityButton.closest('.product-form__quantity, .cart-item__actions')?.querySelector('[data-quantity-status]');
      if (status) status.hidden = !(quantityButton.hasAttribute('data-quantity-plus') && requested > maximum);
      quantityButton.disabled = quantityButton.hasAttribute('data-quantity-plus') && requested >= maximum;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const closeButton = event.target.closest('[data-close-disclosure]');
    if (closeButton) {
      const details = closeButton.closest('details');
      if (details) details.open = false;
      return;
    }

    const thumbnail = event.target.closest('[data-gallery-thumbnail]');
    if (thumbnail) {
      showProductMedia(thumbnail.closest('[data-product-gallery]'), thumbnail.dataset.galleryThumbnail);
      return;
    }

    const galleryArrow = event.target.closest('[data-gallery-prev], [data-gallery-next]');
    if (galleryArrow) {
      const gallery = galleryArrow.closest('[data-product-gallery]');
      const thumbnails = Array.from(gallery?.querySelectorAll('[data-gallery-thumbnail]') || []);
      if (!gallery || thumbnails.length === 0) return;
      const activeIndex = thumbnails.findIndex((button) => button.getAttribute('aria-pressed') === 'true');
      const direction = galleryArrow.hasAttribute('data-gallery-next') ? 1 : -1;
      const nextIndex = (Math.max(activeIndex, 0) + direction + thumbnails.length) % thumbnails.length;
      showProductMedia(gallery, thumbnails[nextIndex].dataset.galleryThumbnail);
    }
  });

  const syncBodyLock = () => {
    const lockingDisclosure = document.querySelector('.mobile-menu[open], .filter-drawer[open]');
    const overlayDisclosure = document.querySelector('.mobile-menu[open], .filter-drawer[open], .language-selector[open], .header-search[open]');
    body.classList.toggle('is-locked', Boolean(lockingDisclosure));
    body.classList.toggle('has-open-overlay', Boolean(overlayDisclosure));
  };

  document.querySelectorAll('[data-disclosure], .filter-drawer, .language-selector').forEach((details) => {
    details.addEventListener('toggle', () => {
      syncBodyLock();
      syncChatOffset();
      if (details.open && details.classList.contains('header-search')) {
        window.setTimeout(() => details.querySelector('input[type="search"]')?.focus(), 40);
      }
    });
  });

  const desktopMenuQuery = window.matchMedia('(min-width: 1101px)');
  const closeMobileMenuOnDesktop = () => {
    if (!desktopMenuQuery.matches) return;
    document.querySelectorAll('.mobile-menu[open]').forEach((menu) => {
      menu.open = false;
    });
    syncBodyLock();
  };
  if (typeof desktopMenuQuery.addEventListener === 'function') {
    desktopMenuQuery.addEventListener('change', closeMobileMenuOnDesktop);
  } else if (typeof desktopMenuQuery.addListener === 'function') {
    desktopMenuQuery.addListener(closeMobileMenuOnDesktop);
  }
  closeMobileMenuOnDesktop();

  document.querySelectorAll('.filter-drawer').forEach((drawer, drawerIndex) => {
    const trigger = drawer.querySelector(':scope > summary');
    const panel = drawer.querySelector(':scope > .filter-drawer__panel');
    if (!trigger || !panel) return;

    if (!panel.id) panel.id = `FilterDrawerPanel-${drawerIndex + 1}`;
    const heading = panel.querySelector('h2');
    if (heading) {
      if (!heading.id) heading.id = `FilterDrawerTitle-${drawerIndex + 1}`;
      if (!panel.hasAttribute('aria-labelledby')) panel.setAttribute('aria-labelledby', heading.id);
    }
    trigger.setAttribute('aria-controls', panel.id);
    trigger.setAttribute('aria-haspopup', 'dialog');
    trigger.setAttribute('aria-expanded', String(drawer.open));
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute('tabindex', '-1');

    const focusableSelector = 'a[href], button:not([disabled]), summary, input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const focusableElements = () => Array.from(panel.querySelectorAll(focusableSelector))
      .filter((element) => element.getClientRects().length > 0 && element.getAttribute('aria-hidden') !== 'true');
    let returnFocus = trigger;

    const viewport = window.visualViewport;
    let viewportFrame = 0;
    let viewportTracking = false;

    const syncViewport = () => {
      if (!drawer.open || (viewport && Math.abs(viewport.scale - 1) > 0.01)) return;
      const height = viewport?.height || window.innerHeight;
      const top = viewport?.offsetTop || 0;
      if (!Number.isFinite(height) || height <= 0 || !Number.isFinite(top)) return;
      panel.style.setProperty('--filter-viewport-height', `${height}px`);
      panel.style.setProperty('--filter-viewport-top', `${Math.max(0, top)}px`);
    };
    const queueViewportSync = () => {
      if (!drawer.open || viewportFrame) return;
      viewportFrame = window.requestAnimationFrame(() => {
        viewportFrame = 0;
        syncViewport();
      });
    };
    const startViewportTracking = () => {
      if (viewportTracking) return;
      viewportTracking = true;
      syncViewport();
      viewport?.addEventListener('resize', queueViewportSync, { passive: true });
      viewport?.addEventListener('scroll', queueViewportSync, { passive: true });
      window.addEventListener('resize', queueViewportSync, { passive: true });
    };
    const stopViewportTracking = () => {
      viewportTracking = false;
      if (viewportFrame) window.cancelAnimationFrame(viewportFrame);
      viewportFrame = 0;
      viewport?.removeEventListener('resize', queueViewportSync);
      viewport?.removeEventListener('scroll', queueViewportSync);
      window.removeEventListener('resize', queueViewportSync);
      panel.style.removeProperty('--filter-viewport-height');
      panel.style.removeProperty('--filter-viewport-top');
    };

    drawer.addEventListener('toggle', () => {
      trigger.setAttribute('aria-expanded', String(drawer.open));
      if (drawer.open) {
        startViewportTracking();
        returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : trigger;
        window.requestAnimationFrame(() => {
          if (!drawer.open) return;
          const firstTarget = panel.querySelector('[data-close-disclosure]') || focusableElements()[0] || panel;
          firstTarget.focus({ preventScroll: true });
        });
      } else {
        stopViewportTracking();
        if (panel.contains(document.activeElement)) {
          const target = returnFocus && returnFocus.isConnected ? returnFocus : trigger;
          target.focus({ preventScroll: true });
        }
      }
    });

    if (drawer.open) startViewportTracking();

    panel.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab' || !drawer.open) return;
      const elements = focusableElements();
      if (elements.length === 0) {
        event.preventDefault();
        panel.focus();
        return;
      }
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const openDisclosure = document.querySelector('details[open].mobile-menu, details[open].filter-drawer, details[open].header-search, details[open][data-language-selector]');
    if (openDisclosure) {
      openDisclosure.open = false;
      openDisclosure.querySelector('summary')?.focus();
    }
  });

  const showProductMedia = (gallery, mediaId) => {
    if (!gallery || !mediaId) return;
    gallery.querySelectorAll('[data-media-id]').forEach((media) => {
      const active = media.dataset.mediaId === String(mediaId);
      media.hidden = !active;
      media.classList.toggle('is-active', active);
      if (!active) {
        media.querySelectorAll('video, audio').forEach((player) => player.pause());
        media.querySelectorAll('iframe').forEach((frame) => {
          frame.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: '' }), '*');
          frame.contentWindow?.postMessage(JSON.stringify({ method: 'pause' }), '*');
        });
      }
    });
    let activeThumbnail;
    gallery.querySelectorAll('[data-gallery-thumbnail]').forEach((button) => {
      const active = button.dataset.galleryThumbnail === String(mediaId);
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
      if (active) activeThumbnail = button;
    });
    activeThumbnail?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  };

  const showAdjacentProductMedia = (gallery, direction) => {
    const thumbnails = Array.from(gallery?.querySelectorAll('[data-gallery-thumbnail]') || []);
    if (!gallery || thumbnails.length < 2) return;
    const activeIndex = thumbnails.findIndex((button) => button.getAttribute('aria-pressed') === 'true');
    const nextIndex = (Math.max(activeIndex, 0) + direction + thumbnails.length) % thumbnails.length;
    showProductMedia(gallery, thumbnails[nextIndex].dataset.galleryThumbnail);
  };

  document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
    const stage = gallery.querySelector('.product-gallery__stage');
    if (!stage) return;
    let touchStartX = 0;
    let touchStartY = 0;

    stage.addEventListener('touchstart', (event) => {
      if (!window.matchMedia('(max-width: 749px)').matches || event.touches.length !== 1) return;
      touchStartX = event.touches[0].clientX;
      touchStartY = event.touches[0].clientY;
    }, { passive: true });

    stage.addEventListener('touchend', (event) => {
      if (!window.matchMedia('(max-width: 749px)').matches || event.changedTouches.length !== 1) return;
      const deltaX = event.changedTouches[0].clientX - touchStartX;
      const deltaY = event.changedTouches[0].clientY - touchStartY;
      if (Math.abs(deltaX) < 45 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
      gallery.dataset.suppressZoomUntil = String(Date.now() + 500);
      showAdjacentProductMedia(gallery, deltaX < 0 ? 1 : -1);
    }, { passive: true });
  });

  document.querySelectorAll('[data-product-root]').forEach((productRoot) => {
    const variantSelect = productRoot.querySelector('[data-variant-select]');
    if (!variantSelect) return;

    variantSelect.addEventListener('change', () => {
      const option = variantSelect.selectedOptions[0];
      if (!option) return;
      const available = option.dataset.available === 'true';
      const currentPrice = productRoot.querySelector('[data-current-price]');
      const comparePrice = productRoot.querySelector('[data-compare-price]');
      const stockBadge = productRoot.querySelector('[data-stock-badge]');
      const addButton = productRoot.querySelector('[data-add-to-cart]');
      const addText = productRoot.querySelector('[data-add-to-cart-text]');
      const stickyPrice = productRoot.querySelector('[data-sticky-price]');
      const stickyAdd = productRoot.querySelector('[data-sticky-add]');
      const saving = productRoot.querySelector('[data-product-saving]');
      const waitlist = productRoot.querySelector('[data-product-waitlist]');
      const deliveryEstimate = productRoot.querySelector('[data-delivery-estimate]');
      const expressCheckout = productRoot.querySelector('[data-express-checkout]');
      const paypalLater = productRoot.querySelector('[data-paypal-later]');
      const paypalMessage = paypalLater?.querySelector('[data-pp-message]');
      const quantityInput = productRoot.querySelector('[data-quantity-input]');
      const quantityStatus = productRoot.querySelector('[data-quantity-status]');

      if (currentPrice) currentPrice.textContent = option.dataset.price || '';
      if (stickyPrice) stickyPrice.textContent = option.dataset.price || '';
      if (comparePrice) {
        comparePrice.textContent = option.dataset.comparePrice || '';
        comparePrice.classList.toggle('is-hidden', !option.dataset.comparePrice);
      }
      if (saving) {
        saving.textContent = option.dataset.saving || '';
        saving.hidden = !option.dataset.saving;
      }
      if (waitlist) waitlist.hidden = available;
      if (deliveryEstimate) deliveryEstimate.hidden = !available;
      if (expressCheckout) expressCheckout.hidden = !available;
      if (paypalLater) paypalLater.hidden = !available;
      if (paypalMessage && option.dataset.paypalAmount) {
        paypalMessage.setAttribute('data-pp-amount', option.dataset.paypalAmount);
      }
      if (quantityInput) {
        const inventoryLimit = Number.parseInt(option.dataset.inventoryLimit, 10);
        if (Number.isNaN(inventoryLimit)) quantityInput.removeAttribute('max');
        else {
          quantityInput.max = String(inventoryLimit);
          quantityInput.value = String(Math.min(Math.max(1, Number.parseInt(quantityInput.value, 10) || 1), inventoryLimit));
        }
        if (quantityStatus) quantityStatus.hidden = true;
        const plus = quantityInput.closest('.quantity')?.querySelector('[data-quantity-plus]');
        if (plus) plus.disabled = !available || (!Number.isNaN(inventoryLimit) && Number(quantityInput.value) >= inventoryLimit);
      }
      if (stockBadge) {
        stockBadge.classList.toggle('stock-badge--sold', !available);
        stockBadge.innerHTML = `<span></span>${available ? (window.vekoStrings?.inStock || 'In stock') : (window.vekoStrings?.soldOut || 'Sold out')}`;
      }
      if (addButton) addButton.disabled = !available;
      if (addText) addText.textContent = available ? (window.vekoStrings?.add || 'Add to cart') : (window.vekoStrings?.soldOut || 'Sold out');
      if (stickyAdd) {
        stickyAdd.disabled = !available;
        stickyAdd.textContent = available ? (window.vekoStrings?.add || 'Add to cart') : (window.vekoStrings?.soldOut || 'Sold out');
      }
      if (option.dataset.featuredMediaId) {
        showProductMedia(productRoot.querySelector('[data-product-gallery]'), option.dataset.featuredMediaId);
      }
      if (option.dataset.url && !window.Shopify?.designMode) {
        window.history.replaceState({}, '', option.dataset.url);
      }
    });
  });

  const normalizeQuantityInput = (input, { allowEmpty = false } = {}) => {
    if (allowEmpty && input.value === '') return null;
    const minimum = Number.parseInt(input.min, 10);
    const maximum = Number.parseInt(input.max, 10);
    const safeMinimum = Number.isNaN(minimum) ? 0 : minimum;
    const requested = Number.parseInt(input.value, 10);
    let safeValue = Number.isNaN(requested) ? safeMinimum : Math.max(safeMinimum, requested);
    if (!Number.isNaN(maximum)) safeValue = Math.min(maximum, safeValue);
    input.value = String(safeValue);
    const container = input.closest('.product-form__quantity, .cart-item__actions');
    const status = container?.querySelector('[data-quantity-status]');
    if (status) status.hidden = Number.isNaN(maximum) || requested <= maximum;
    const plus = container?.querySelector('[data-quantity-plus]');
    if (plus) plus.disabled = !Number.isNaN(maximum) && safeValue >= maximum;
    return safeValue;
  };

  document.addEventListener('input', (event) => {
    const input = event.target.closest?.('[data-quantity-input]');
    if (input) normalizeQuantityInput(input, { allowEmpty: true });
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest?.('[data-quantity-input]');
    if (input) normalizeQuantityInput(input);
  });

  document.querySelectorAll('[data-auto-submit]').forEach((input) => {
    input.addEventListener('change', () => input.form?.submit());
  });

  document.querySelectorAll('[data-language-option]').forEach((button) => {
    button.addEventListener('click', () => {
      try {
        window.localStorage.setItem('veko_language_preference', button.value);
      } catch (error) {
        // Language selection still works when storage is unavailable.
      }
    });
  });

  const headerSearchForm = document.querySelector('[data-header-search-form]');
  const predictiveInput = document.querySelector('#HeaderSearch');
  const predictivePanel = document.querySelector('[data-predictive-search]');
  let predictiveTimer;
  let predictiveRequestId = 0;
  let predictiveController;
  if (predictiveInput && predictivePanel) {
    headerSearchForm?.addEventListener('submit', (event) => {
      if (predictiveInput.value.trim()) return;
      event.preventDefault();
      predictiveInput.focus();
    });
    predictiveInput.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' || !predictiveInput.value.trim()) return;
      event.preventDefault();
      headerSearchForm?.requestSubmit(headerSearchForm.querySelector('[data-header-search-submit]'));
    });
    predictiveInput.addEventListener('input', () => {
      window.clearTimeout(predictiveTimer);
      const query = predictiveInput.value.trim();
      predictiveRequestId += 1;
      predictiveController?.abort();
      if (query.length < 2) {
        predictivePanel.hidden = true;
        predictivePanel.innerHTML = '';
        predictivePanel.removeAttribute('aria-busy');
        return;
      }
      const requestId = predictiveRequestId;
      predictiveTimer = window.setTimeout(async () => {
        predictiveController = new AbortController();
        predictivePanel.setAttribute('aria-busy', 'true');
        try {
          const searchRoute = window.vekoRoutes?.search || joinRoute(window.vekoRoutes?.root || '/', 'search');
          const response = await fetch(`${joinRoute(searchRoute, 'suggest.json')}?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=5`, {
            headers: { Accept: 'application/json' },
            signal: predictiveController.signal
          });
          if (!response.ok) throw new Error('Predictive search unavailable');
          const payload = await response.json();
          if (requestId !== predictiveRequestId || predictiveInput.value.trim() !== query) return;
          const products = payload.resources?.results?.products || [];
          predictivePanel.innerHTML = '';
          if (!products.length) {
            const empty = document.createElement('p');
            empty.className = 'predictive-search__empty';
            empty.textContent = window.vekoStrings?.noSearchResults || '';
            predictivePanel.appendChild(empty);
          } else {
            products.forEach((product) => {
              const link = document.createElement('a');
              link.className = 'predictive-search__item';
              link.href = product.url;
              const productImage = typeof product.image === 'string' ? product.image : (product.image?.url || product.featured_image?.url || '');
              if (productImage) {
                const image = document.createElement('img');
                image.src = productImage;
                image.alt = '';
                image.loading = 'lazy';
                image.decoding = 'async';
                image.width = 72;
                image.height = 72;
                link.appendChild(image);
              } else {
                link.appendChild(document.createElement('span'));
              }
              const title = document.createElement('span');
              title.textContent = product.title;
              const price = document.createElement('strong');
              const numericPrice = Number(String(product.price || '').replace(',', '.'));
              price.textContent = Number.isFinite(numericPrice)
                ? new Intl.NumberFormat(document.documentElement.lang || 'de', { style: 'currency', currency: 'EUR' }).format(numericPrice)
                : '';
              link.append(title, price);
              predictivePanel.appendChild(link);
            });
          }
          predictivePanel.hidden = false;
        } catch (error) {
          if (error.name === 'AbortError' || requestId !== predictiveRequestId) return;
          predictivePanel.innerHTML = '';
          const fallback = document.createElement('a');
          fallback.className = 'predictive-search__empty';
          const searchRoute = window.vekoRoutes?.search || joinRoute(window.vekoRoutes?.root || '/', 'search');
          fallback.href = `${searchRoute}?q=${encodeURIComponent(query)}`;
          fallback.textContent = window.vekoStrings?.viewSearchResults || query;
          predictivePanel.appendChild(fallback);
          predictivePanel.hidden = false;
        } finally {
          if (requestId === predictiveRequestId) predictivePanel.removeAttribute('aria-busy');
        }
      }, 220);
    });
  }

  const recentSection = document.querySelector('[data-recently-viewed]');
  if (recentSection) {
    try {
      const recentKey = `veko_recent_products_v2_${(document.documentElement.lang || 'de').split('-')[0]}`;
      const stored = JSON.parse(window.localStorage.getItem(recentKey) || '[]');
      const current = {
        handle: recentSection.dataset.handle,
        title: recentSection.dataset.title,
        url: recentSection.dataset.url,
        price: recentSection.dataset.price,
        image: recentSection.dataset.image
      };
      const previous = stored.filter((item) => item.handle && item.handle !== current.handle).slice(0, 4);
      const grid = recentSection.querySelector('[data-recently-viewed-grid]');
      previous.forEach((item) => {
        const link = document.createElement('a');
        link.className = 'recently-viewed__card';
        link.href = item.url;
        if (item.image) {
          const image = document.createElement('img');
          image.src = item.image;
          image.alt = item.title;
          image.loading = 'lazy';
          image.decoding = 'async';
          image.width = 160;
          image.height = 160;
          link.appendChild(image);
        }
        const content = document.createElement('span');
        const title = document.createElement('strong');
        title.textContent = item.title;
        const price = document.createElement('strong');
        price.className = 'recently-viewed__price';
        price.textContent = item.price;
        content.append(title, price);
        link.appendChild(content);
        grid.appendChild(link);
      });
      recentSection.hidden = previous.length === 0;
      window.localStorage.setItem(recentKey, JSON.stringify([current, ...previous].slice(0, 8)));
    } catch (error) {
      recentSection.hidden = true;
    }
  }

  const germanHolidayCache = new Map();
  const dateKey = (date) => [
    date.getUTCFullYear(),
    String(date.getUTCMonth() + 1).padStart(2, '0'),
    String(date.getUTCDate()).padStart(2, '0')
  ].join('-');

  const easterSundayUtc = (year) => {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(Date.UTC(year, month - 1, day, 12));
  };

  const germanBusinessHolidays = (year) => {
    if (germanHolidayCache.has(year)) return germanHolidayCache.get(year);
    const holidays = new Set([
      `${year}-01-01`,
      `${year}-01-06`,
      `${year}-05-01`,
      `${year}-08-15`,
      `${year}-10-03`,
      `${year}-11-01`,
      `${year}-12-25`,
      `${year}-12-26`
    ]);
    const easter = easterSundayUtc(year);
    [-2, 1, 39, 50, 60].forEach((offset) => {
      const holiday = new Date(easter);
      holiday.setUTCDate(holiday.getUTCDate() + offset);
      holidays.add(dateKey(holiday));
    });
    germanHolidayCache.set(year, holidays);
    return holidays;
  };

  const isGermanBusinessDay = (date) => {
    const day = date.getUTCDay();
    return day !== 0 && day !== 6 && !germanBusinessHolidays(date.getUTCFullYear()).has(dateKey(date));
  };

  const addBusinessDays = (start, days) => {
    const date = new Date(start);
    let added = 0;
    while (added < days) {
      date.setUTCDate(date.getUTCDate() + 1);
      if (isGermanBusinessDay(date)) added += 1;
    }
    return date;
  };

  const nextBusinessDay = (start) => {
    const date = new Date(start);
    do {
      date.setUTCDate(date.getUTCDate() + 1);
    } while (!isGermanBusinessDay(date));
    return date;
  };

  const berlinDateParts = () => {
    const now = new Date();
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/Berlin',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hourCycle: 'h23'
    }).formatToParts(now).reduce((values, part) => {
      if (part.type !== 'literal') values[part.type] = Number(part.value);
      return values;
    }, {});
  };

  const deliveryEstimate = document.querySelector('[data-delivery-estimate]');
  if (deliveryEstimate) {
    const deliveryTitle = deliveryEstimate.querySelector('[data-delivery-estimate-title]') || document.querySelector('[data-delivery-estimate-title]');
    const deliveryDetail = deliveryEstimate.querySelector('[data-delivery-estimate-detail]') || document.querySelector('[data-delivery-estimate-detail]');
    const locale = document.documentElement.lang || 'de';
    const language = locale.split('-')[0];
    const fallbackCopy = {
      de: {
        title: 'Lieferung:',
        before: '__DATE__',
        after: '__DATE__'
      },
      it: {
        title: 'Consegna:',
        before: '__DATE__',
        after: '__DATE__'
      },
      en: {
        title: 'Delivery:',
        before: '__DATE__',
        after: '__DATE__'
      }
    };
    const copy = fallbackCopy[language] || fallbackCopy.de;
    const interpolate = (template, replacements) => Object.entries(replacements)
      .reduce((value, [placeholder, replacement]) => value.split(placeholder).join(replacement), String(template || ''));

    const renderDeliveryEstimate = () => {
      const parts = berlinDateParts();
      const today = new Date(Date.UTC(parts.year, parts.month - 1, parts.day, 12));
      const beforeCutoff = isGermanBusinessDay(today) && parts.hour < 12;
      const handlingStart = beforeCutoff ? today : nextBusinessDay(today);
      const handlingEnd = nextBusinessDay(handlingStart);
      const deliveryStart = addBusinessDays(handlingStart, 1);
      const deliveryEnd = addBusinessDays(handlingEnd, 2);
      const formatter = new Intl.DateTimeFormat(locale, {
        day: 'numeric',
        month: 'short',
        timeZone: 'UTC'
      });
      const dateRange = `${formatter.format(deliveryStart)}–${formatter.format(deliveryEnd)}`;

      if (deliveryTitle || deliveryDetail) {
        const titleText = window.vekoStrings?.deliveryFastTitle || copy.title;
        if (deliveryTitle && deliveryTitle.textContent !== titleText) deliveryTitle.textContent = titleText;
        if (deliveryDetail) {
          const template = beforeCutoff
            ? (window.vekoStrings?.deliveryBeforeCutoff || copy.before)
            : (window.vekoStrings?.deliveryAfterCutoff || copy.after);
          const detailText = interpolate(template, { __DATE__: dateRange });
          if (deliveryDetail.textContent !== detailText) deliveryDetail.textContent = detailText;
        }
      } else if (window.vekoStrings?.deliveryEstimate) {
        const estimateText = window.vekoStrings.deliveryEstimate.replace('__DATE__', dateRange);
        if (deliveryEstimate.textContent !== estimateText) deliveryEstimate.textContent = estimateText;
      }
    };

    let deliveryTimer;
    const scheduleDeliveryRefresh = () => {
      window.clearTimeout(deliveryTimer);
      const delayToNextMinute = 60000 - (Date.now() % 60000) + 50;
      deliveryTimer = window.setTimeout(() => {
        renderDeliveryEstimate();
        scheduleDeliveryRefresh();
      }, delayToNextMinute);
    };
    renderDeliveryEstimate();
    scheduleDeliveryRefresh();
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) renderDeliveryEstimate();
    });
  }

  const localizeAppContent = (scope = document) => {
    const nodes = [];
    if (scope instanceof Element && scope.matches('.htusb-payment-badges__msg')) nodes.push(scope);
    if (scope.querySelectorAll) nodes.push(...scope.querySelectorAll('.htusb-payment-badges__msg'));
    nodes.forEach((node) => {
      if (window.vekoStrings?.paymentBadgeHeading) node.textContent = window.vekoStrings.paymentBadgeHeading;
    });
  };
  localizeAppContent();

  document.querySelectorAll('[data-product-recommendations]').forEach(async (container) => {
    if (!container.dataset.url || container.dataset.recommendationsLoaded === 'true' || container.dataset.recommendationsFallback === 'true') return;
    container.dataset.recommendationsLoaded = 'true';
    try {
      const response = await fetch(container.dataset.url);
      if (!response.ok) return;
      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const replacement = parsed.querySelector('[data-product-recommendations]');
      if (replacement?.innerHTML.trim()) {
        container.innerHTML = replacement.innerHTML;
        window.dispatchEvent(new Event('scroll'));
      }
    } catch (error) {
      // Recommendations are supplementary and should never block the page.
    }
  });

  let lastScroll = window.scrollY;
  const header = document.querySelector('[data-site-header]');
  const mobileBuyBar = document.querySelector('.mobile-buy-bar, .mobile-cart-checkout');
  const primaryProductButton = document.querySelector('[data-add-to-cart]');
  const primaryCartCheckout = document.querySelector('[data-cart-primary-checkout]');
  const recommendations = document.querySelector('[data-product-recommendations]');
  const footer = document.querySelector('.site-footer');
  const syncChatOffset = () => {
    const chat = document.querySelector('shopify-chat');
    if (!chat) return;
    const mobileViewport = window.matchMedia('(max-width: 749px)').matches;
    const commerceInputActive = body.classList.contains('is-commerce-input-active');
    const productBarReady = !mobileBuyBar?.classList.contains('mobile-buy-bar') || mobileBuyBar.classList.contains('is-active');
    const buyBarVisible = mobileViewport && !commerceInputActive && mobileBuyBar && productBarReady && !mobileBuyBar.classList.contains('is-obscured');
    const toastVisible = mobileViewport && toast?.classList.contains('is-visible');
    const chatOffset = toastVisible ? '220px' : (buyBarVisible ? '108px' : '20px');
    chat.style.setProperty('--shopify-agent-activator-offset', chatOffset, 'important');
  };
  const onScroll = () => {
    const current = window.scrollY;
    if (header) {
      header.classList.toggle('is-scrolled', current > 8);
      header.classList.toggle('is-moving-down', current > lastScroll && current > 160);
    }
    if (mobileBuyBar) {
      if (mobileBuyBar.classList.contains('mobile-buy-bar')) {
        const productButtonPassed = primaryProductButton && primaryProductButton.getBoundingClientRect().bottom < 90;
        mobileBuyBar.classList.toggle('is-active', Boolean(productButtonPassed));
      }
      const primaryCartCheckoutRect = primaryCartCheckout?.getBoundingClientRect();
      const primaryCartCheckoutVisible = mobileBuyBar.classList.contains('mobile-cart-checkout') &&
        primaryCartCheckoutRect &&
        primaryCartCheckoutRect.top < window.innerHeight &&
        primaryCartCheckoutRect.bottom > 0;
      const recommendationsVisible = recommendations &&
        recommendations.innerHTML.trim() &&
        recommendations.getBoundingClientRect().top < window.innerHeight * 0.92;
      const footerVisible = footer && footer.getBoundingClientRect().top < window.innerHeight;
      mobileBuyBar.classList.toggle('is-obscured', Boolean(primaryCartCheckoutVisible || recommendationsVisible || footerVisible));
    }
    syncChatOffset();
    lastScroll = current;
  };
  let scrollFrame = 0;
  const requestScrollUpdate = () => {
    if (scrollFrame) return;
    scrollFrame = window.requestAnimationFrame(() => {
      scrollFrame = 0;
      onScroll();
    });
  };
  window.addEventListener('scroll', requestScrollUpdate, { passive: true });
  window.addEventListener('resize', requestScrollUpdate, { passive: true });

  const commerceInputSelector = '[data-discount-input], .cart-note-disclosure textarea, .cart-item [data-quantity-input]';
  const syncCommerceInputState = () => {
    const inputActive = window.matchMedia('(max-width: 749px)').matches &&
      document.activeElement instanceof Element &&
      document.activeElement.matches(commerceInputSelector);
    body.classList.toggle('is-commerce-input-active', inputActive);
    syncChatOffset();
  };
  document.addEventListener('focusin', (event) => {
    if (event.target instanceof Element && event.target.matches(commerceInputSelector)) syncCommerceInputState();
  });
  document.addEventListener('focusout', (event) => {
    if (event.target instanceof Element && event.target.matches(commerceInputSelector)) {
      window.setTimeout(syncCommerceInputState, 0);
    }
  });
  window.addEventListener('resize', syncCommerceInputState, { passive: true });

  const chatObserver = new MutationObserver((mutations) => {
    syncChatOffset();
    mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) localizeAppContent(node);
    }));
  });
  chatObserver.observe(document.body, { childList: true, subtree: true });
  onScroll();

  const cartCheckoutForm = document.querySelector('#VekoCartForm[data-checkout-url]');
  if (cartCheckoutForm) {
    cartCheckoutForm.addEventListener('submit', async (event) => {
      if (event.submitter?.name !== 'checkout') return;
      event.preventDefault();
      if (cartCheckoutForm.dataset.checkoutInProgress === 'true') return;
      cartCheckoutForm.dataset.checkoutInProgress = 'true'; setFormLoading(cartCheckoutForm, true);
      try { const updates = {}; cartCheckoutForm.querySelectorAll('[data-quantity-input][data-line-key]').forEach((input) => { updates[input.dataset.lineKey] = Math.max(0, Number.parseInt(input.value, 10) || 0); }); const response = await fetch(routeWithJson(cartCheckoutForm.dataset.cartUpdateUrl), { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ updates, note: cartCheckoutForm.querySelector('textarea[name="note"]')?.value || '' }) }); if (!response.ok) throw new Error('cart-update-failed'); window.location.assign(cartCheckoutForm.dataset.checkoutUrl); } catch (error) { setFormLoading(cartCheckoutForm, false); delete cartCheckoutForm.dataset.checkoutInProgress; showToast(window.vekoStrings?.error || 'Das hat leider nicht funktioniert.', true); }
    });
  }

  const discountForm = document.querySelector('[data-discount-form]');
  if (discountForm) {
    const discountInput = discountForm.querySelector('[data-discount-input]');
    const discountButton = discountForm.querySelector('[data-discount-apply]');
    const discountRemove = document.querySelector('[data-discount-remove]');
    const discountStatus = discountForm.querySelector('[data-discount-status]');
    const discountRow = document.querySelector('[data-cart-discount-row]');
    const discountLabel = discountRow?.querySelector('[data-discount-label]');
    const discountAmount = document.querySelector('[data-cart-discount-amount]');
    const finalTotals = document.querySelectorAll('[data-cart-final-total]');
    const subtotalNode = document.querySelector('[data-cart-subtotal]');
    const savingNode = document.querySelector('[data-cart-saving]');
    const cartForm = document.querySelector('#VekoCartForm');
    const cartLiveStatus = document.querySelector('[data-cart-live-status]');
    const cartPaypalMessage = document.querySelector('[data-cart-paypal-later] [data-pp-message]');
    let currentDiscount = Number(discountForm.dataset.currentDiscount || 0);
    let referenceTotal = Number(discountForm.dataset.referenceTotal || 0);
    let discountRequestInFlight = false;
    const formatMoney = (cents, currency = 'EUR') => new Intl.NumberFormat(document.documentElement.lang || 'de', {
      style: 'currency', currency
    }).format(Number(cents || 0) / 100);

    const setDiscountStatus = (message = '', state = '') => {
      if (!discountStatus) return;
      discountStatus.textContent = message;
      discountStatus.classList.remove('is-success', 'is-error', 'is-neutral');
      if (state) discountStatus.classList.add(`is-${state}`);
      if (state === 'error') discountInput?.setAttribute('aria-invalid', 'true');
      else discountInput?.removeAttribute('aria-invalid');
    };

    const synchronizedCartFields = () => {
      const updates = {};
      let synchronizedReferenceTotal = 0;
      document.querySelectorAll('#VekoCartForm [data-quantity-input][data-line-key]').forEach((input) => {
        const quantity = Math.max(0, Number.parseInt(input.value, 10) || 0);
        updates[input.dataset.lineKey] = quantity;
        synchronizedReferenceTotal += quantity * Number(input.dataset.referenceUnit || 0);
      });
      const note = document.querySelector('#VekoCartForm textarea[name="note"]')?.value || '';
      if (Object.keys(updates).length && synchronizedReferenceTotal > 0) referenceTotal = synchronizedReferenceTotal;
      return { updates, note };
    };

    const updateCartPresentation = (updatedCart, currency) => {
      const updatedItems = new Map((updatedCart.items || []).map((item) => [item.key, item]));
      const availableItems = [...(updatedCart.items || [])];
      const claimedItems = new Set();
      document.querySelectorAll('[data-cart-line-key]').forEach((line) => {
        let item = updatedItems.get(line.dataset.cartLineKey);
        if (!item) {
          item = availableItems.find((candidate) =>
            !claimedItems.has(candidate) && String(candidate.variant_id) === line.dataset.cartLineVariant
          );
        }
        if (!item) {
          line.remove();
          return;
        }
        claimedItems.add(item);
        line.dataset.cartLineKey = item.key;
        line.querySelectorAll('[data-cart-line-price]').forEach((price) => {
          price.textContent = formatMoney(item.final_line_price, currency);
        });
        const originalPrice = line.querySelector('[data-cart-line-original-price]');
        if (originalPrice) {
          const hasLineDiscount = Number(item.original_line_price || 0) > Number(item.final_line_price || 0);
          originalPrice.hidden = !hasLineDiscount;
          originalPrice.textContent = hasLineDiscount ? formatMoney(item.original_line_price, currency) : '';
        }
        const quantityInput = line.querySelector('[data-quantity-input]');
        if (quantityInput) {
          quantityInput.value = String(item.quantity);
          quantityInput.dataset.lineKey = item.key;
        }
      });
      if (subtotalNode) subtotalNode.textContent = formatMoney(updatedCart.items_subtotal_price, currency);
      document.querySelectorAll('[data-cart-count]').forEach((count) => {
        count.textContent = updatedCart.item_count;
        count.classList.toggle('is-empty', updatedCart.item_count === 0);
      });
    };

    const updateCartTotals = (updatedCart, currency) => {
      currentDiscount = Number(updatedCart.total_discount || 0);
      discountForm.dataset.currentDiscount = String(currentDiscount);
      if (discountAmount) discountAmount.textContent = currentDiscount > 0 ? `-${formatMoney(currentDiscount, currency)}` : '';
      if (discountRow) discountRow.hidden = currentDiscount <= 0;
      finalTotals.forEach((finalTotal) => {
        finalTotal.textContent = formatMoney(updatedCart.total_price, currency);
      });
      if (cartPaypalMessage) {
        cartPaypalMessage.setAttribute('data-pp-amount', (Number(updatedCart.total_price || 0) / 100).toFixed(2));
      }
      referenceTotal = [...document.querySelectorAll('#VekoCartForm [data-quantity-input][data-reference-unit]')]
        .reduce((sum, input) => sum + (Number.parseInt(input.value, 10) || 0) * Number(input.dataset.referenceUnit || 0), 0);
      const totalSaving = Math.max(0, referenceTotal - Number(updatedCart.total_price || 0));
      if (savingNode) {
        savingNode.hidden = totalSaving <= 0;
        savingNode.textContent = totalSaving > 0
          ? (discountForm.dataset.savingLabel || '__AMOUNT__').replace('__AMOUNT__', formatMoney(totalSaving, currency))
          : '';
      }
    };

    let quantityRequestId = 0;
    const updateLineQuantity = async (input) => {
      const quantity = normalizeQuantityInput(input);
      if (quantity === null || !input.dataset.lineKey) return;
      const requestId = ++quantityRequestId;
      const cartUrl = discountForm.dataset.cartUrl || '/cart';
      const changeUrl = `${cartUrl.replace(/\/cart\/?$/, '')}/cart/change.js`;
      const previousQuantity = Number.parseInt(input.dataset.committedQuantity || input.defaultValue, 10) || 0;
      cartForm?.setAttribute('aria-busy', 'true');
      if (cartLiveStatus) cartLiveStatus.textContent = cartLiveStatus.dataset.updatingMessage || '';
      try {
        const response = await fetch(changeUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ id: input.dataset.lineKey, quantity })
        });
        if (!response.ok) {
          const problem = await response.json().catch(() => ({}));
          throw new Error(problem.description || problem.message || 'Cart update failed');
        }
        const updatedCart = await response.json();
        if (requestId !== quantityRequestId) return;
        const currency = updatedCart.currency || 'EUR';
        updateCartPresentation(updatedCart, currency);
        updateCartTotals(updatedCart, currency);
        document.querySelectorAll('#VekoCartForm [data-quantity-input]').forEach((cartInput) => {
          cartInput.dataset.committedQuantity = cartInput.value;
          cartInput.defaultValue = cartInput.value;
        });
        if (cartLiveStatus) cartLiveStatus.textContent = cartLiveStatus.dataset.updatedMessage || '';
        if (Number(updatedCart.item_count || 0) === 0) window.location.assign(cartUrl);
      } catch (error) {
        input.value = String(previousQuantity);
        normalizeQuantityInput(input);
        if (cartLiveStatus) cartLiveStatus.textContent = error.message || cartLiveStatus.dataset.errorMessage || '';
      } finally {
        if (requestId === quantityRequestId) cartForm?.removeAttribute('aria-busy');
      }
    };

    document.querySelectorAll('#VekoCartForm [data-quantity-input][data-line-key]').forEach((input) => {
      input.dataset.committedQuantity = input.value;
      input.addEventListener('change', () => updateLineQuantity(input));
    });

    const updateDiscount = async (code) => {
      if (discountRequestInFlight) return;
      discountRequestInFlight = true;
      const cartUrl = discountForm.dataset.cartUrl || '/cart';
      const updateUrl = `${cartUrl.replace(/\/cart\/?$/, '')}/cart/update.js`;
      if (discountButton) discountButton.disabled = true;
      if (discountRemove) discountRemove.disabled = true;
      discountForm.setAttribute('aria-busy', 'true');
      setDiscountStatus();
      try {
        const synchronizedFields = synchronizedCartFields();
        const response = await fetch(updateUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ ...synchronizedFields, discount: code })
        });
        if (!response.ok) throw new Error('Discount update failed');
        const updatedCart = await response.json();
        const newDiscount = Number(updatedCart.total_discount || 0);
        const currency = updatedCart.currency || 'EUR';
        const applications = [
          ...(updatedCart.cart_level_discount_applications || []),
          ...(updatedCart.items || []).flatMap((item) => item.line_level_discount_allocations || []).map((allocation) => allocation.discount_application || {})
        ];
        const codeApplications = applications.filter((application) => application.type === 'discount_code');
        const automaticApplications = applications.filter((application) => application.type === 'automatic');
        const normalizedCode = String(code || '').trim().toLowerCase();
        const namedMatch = applications.some((application) => String(application.title || '').trim().toLowerCase() === normalizedCode);
        const discountCodes = Array.isArray(updatedCart.discount_codes) ? updatedCart.discount_codes : null;
        const matchedCode = discountCodes?.find((discountCode) => String(discountCode.code || '').trim().toLowerCase() === normalizedCode);
        const explicitlyInvalid = Boolean(code) && (matchedCode?.applicable === false || (discountCodes && !matchedCode));
        const explicitlyApplicable = matchedCode?.applicable === true;
        const applied = !code || explicitlyApplicable || namedMatch || newDiscount > currentDiscount;
        const verifiedAtCheckout = Boolean(code) && !explicitlyInvalid && !applied;

        const codeApplied = Boolean(code) && !explicitlyInvalid && (explicitlyApplicable || namedMatch || codeApplications.length > 0 || newDiscount > currentDiscount);
        const hasVisibleDiscount = newDiscount > 0 || verifiedAtCheckout;
        if (discountRow) discountRow.hidden = !hasVisibleDiscount;
        if (discountRemove) discountRemove.hidden = !codeApplied;
        if (discountLabel) {
          const visibleTitle = codeApplications[0]?.title || automaticApplications[0]?.title || discountForm.dataset.discountLabel || '';
          discountLabel.childNodes[0].textContent = visibleTitle ? `${visibleTitle} ` : `${discountForm.dataset.discountLabel || ''} `;
        }
        if (discountAmount) discountAmount.textContent = newDiscount > 0 ? `-${formatMoney(newDiscount, currency)}` : '';
        updateCartPresentation(updatedCart, currency);
        finalTotals.forEach((finalTotal) => {
          finalTotal.textContent = formatMoney(updatedCart.total_price, currency);
        });
        const totalSaving = Math.max(0, referenceTotal - Number(updatedCart.total_price || 0));
        if (savingNode) {
          savingNode.hidden = totalSaving <= 0;
          savingNode.textContent = totalSaving > 0
            ? (discountForm.dataset.savingLabel || '__AMOUNT__').replace('__AMOUNT__', formatMoney(totalSaving, currency))
            : '';
        }
        currentDiscount = newDiscount;
        discountForm.dataset.currentDiscount = String(newDiscount);
        if (!code) {
          setDiscountStatus();
        } else if (explicitlyInvalid) {
          setDiscountStatus(discountForm.dataset.invalidMessage || '', 'error');
        } else if (verifiedAtCheckout) {
          const locale = (document.documentElement.lang || 'de').split('-')[0];
          const fallbackMessages = {
            de: 'Code gespeichert. Die endgültige Berechtigung wird beim Checkout geprüft.',
            it: 'Codice salvato. L\'idoneità finale sarà verificata al pagamento.',
            en: 'Code saved. Final eligibility will be checked at checkout.'
          };
          const configuredMessage = discountForm.dataset.verifiedMessage || '';
          const message = configuredMessage.includes('translation missing') ? fallbackMessages[locale] || fallbackMessages.de : configuredMessage;
          setDiscountStatus(message, 'neutral');
        } else {
          setDiscountStatus();
        }
        if ((applied || verifiedAtCheckout) && discountInput) discountInput.value = '';

        if (Number(updatedCart.item_count || 0) === 0) {
          window.location.assign(cartUrl);
        }
      } catch (error) {
        setDiscountStatus(discountForm.dataset.errorMessage || '', 'error');
      } finally {
        discountRequestInFlight = false;
        if (discountButton) discountButton.disabled = false;
        if (discountRemove) discountRemove.disabled = false;
        discountForm.removeAttribute('aria-busy');
      }
    };
    const applyDiscount = () => {
      const code = discountInput?.value.trim();
      if (!code) return discountInput?.focus();
      updateDiscount(code);
    };
    discountButton?.addEventListener('click', applyDiscount);
    discountRemove?.addEventListener('click', () => updateDiscount(''));
    discountInput?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        applyDiscount();
      }
    });
  }

  if (window.location.hash === '#account-deletion') {
    const contactSection = document.querySelector('[data-account-deletion-message]');
    const contactMessage = contactSection?.querySelector('textarea[name="contact[body]"]');
    if (contactMessage && !contactMessage.value.trim()) {
      contactMessage.value = contactSection.dataset.accountDeletionMessage || '';
    }
    window.requestAnimationFrame(() => contactSection?.scrollIntoView({ block: 'center' }));
  }



  /* Vekoshop product image zoom */
  let vekoZoomOverlay = null;
  let vekoZoomImage = null;
  let vekoZoomLastTrigger = null;

  const closeVekoProductZoom = () => {
    if (!vekoZoomOverlay || vekoZoomOverlay.hidden) return;
    vekoZoomOverlay.hidden = true;
    vekoZoomImage.classList.remove('is-zoomed');
    vekoZoomImage.removeAttribute('src');
    vekoZoomImage.removeAttribute('alt');
    document.body.classList.remove('veko-product-zoom-open');
    if (vekoZoomLastTrigger) vekoZoomLastTrigger.focus({ preventScroll: true });
  };

  const ensureVekoProductZoom = () => {
    if (vekoZoomOverlay) return;

    vekoZoomOverlay = document.createElement('div');
    vekoZoomOverlay.className = 'veko-product-zoom';
    vekoZoomOverlay.hidden = true;
    vekoZoomOverlay.setAttribute('role', 'dialog');
    vekoZoomOverlay.setAttribute('aria-modal', 'true');
    vekoZoomOverlay.setAttribute('aria-label', 'Produktbild vergrößert');
    vekoZoomOverlay.innerHTML = `
      <button class="veko-product-zoom__close" type="button" aria-label="Schließen">×</button>
      <div class="veko-product-zoom__viewport">
        <img class="veko-product-zoom__image" alt="">
      </div>
      <div class="veko-product-zoom__hint" aria-hidden="true">Tocca la foto per ingrandire</div>
    `;
    document.body.appendChild(vekoZoomOverlay);
    vekoZoomImage = vekoZoomOverlay.querySelector('.veko-product-zoom__image');

    vekoZoomOverlay.querySelector('.veko-product-zoom__close').addEventListener('click', closeVekoProductZoom);
    vekoZoomOverlay.addEventListener('click', (event) => {
      if (event.target === vekoZoomOverlay || event.target.classList.contains('veko-product-zoom__viewport')) {
        closeVekoProductZoom();
      }
    });
    vekoZoomImage.addEventListener('click', (event) => {
      event.stopPropagation();
      vekoZoomImage.classList.toggle('is-zoomed');
    });
  };

  const openVekoProductZoom = (triggerImage) => {
    ensureVekoProductZoom();
    const fullSrc = triggerImage.dataset.productZoomImage || triggerImage.currentSrc || triggerImage.src;
    if (!fullSrc) return;
    vekoZoomLastTrigger = triggerImage;
    vekoZoomImage.src = fullSrc;
    vekoZoomImage.alt = triggerImage.alt || '';
    vekoZoomImage.classList.remove('is-zoomed');
    vekoZoomOverlay.hidden = false;
    document.body.classList.add('veko-product-zoom-open');
    vekoZoomOverlay.querySelector('.veko-product-zoom__close').focus({ preventScroll: true });
  };

  document.addEventListener('click', (event) => {
    const image = event.target.closest('[data-product-gallery] .product-media[data-media-type="image"] img[data-product-zoom-image]');
    if (!image) return;
    const gallery = image.closest('[data-product-gallery]');
    if (Number(gallery?.dataset.suppressZoomUntil || 0) > Date.now()) return;
    event.preventDefault();
    openVekoProductZoom(image);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeVekoProductZoom();
  });


  root.classList.add('js-ready');
})();
